import type { Transaction } from "./finance.ts";
import { matchesBrandFilter } from "./business-units.ts";

export type FinanceBudget = {
  id?: string;
  month: string;
  businessUnit: string | null;
  category: string;
  amount: number;
  note?: string;
  createdBy?: string | null;
  updatedAt?: string;
};

export type BudgetUsageItem = {
  category: string;
  budget: number;
  actual: number;
  remaining: number;
  usagePercent: number | null;
};

export type BudgetOverview = {
  month: string;
  businessUnit: string | null;
  budget: number;
  actual: number;
  remaining: number;
  usagePercent: number | null;
  items: BudgetUsageItem[];
};

export type CategoryTotal = {
  category: string;
  amount: number;
  transactionCount: number;
};

export const insightPalette = [
  "#f0785e",
  "#2f80ed",
  "#a9db50",
  "#b96fc2",
  "#ffae4a",
  "#f6cf36",
  "#35b4bd",
  "#258778",
  "#ef334f",
  "#9b3b6c",
] as const;

export function normalizeBudgetMonth(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-01` : "";
}

export function budgetMonthKey(value: string) {
  return normalizeBudgetMonth(value).slice(0, 7);
}

export function categoryTotals(
  transactions: Transaction[],
  direction: Transaction["direction"],
  limit = 10,
) {
  const grouped = new Map<string, CategoryTotal>();
  for (const transaction of transactions) {
    if (transaction.deletedAt || transaction.direction !== direction) continue;
    const current = grouped.get(transaction.category) || {
      category: transaction.category,
      amount: 0,
      transactionCount: 0,
    };
    current.amount += transaction.amount;
    current.transactionCount += 1;
    grouped.set(transaction.category, current);
  }
  return [...grouped.values()]
    .sort((a, b) => b.amount - a.amount || b.transactionCount - a.transactionCount)
    .slice(0, limit);
}

export function calculateBudgetOverview(
  budgets: FinanceBudget[],
  transactions: Transaction[],
  month: string,
  businessUnit: string | null,
): BudgetOverview {
  const normalizedMonth = normalizeBudgetMonth(month);
  const monthKey = normalizedMonth.slice(0, 7);
  const selectedBudgets = budgets.filter((item) =>
    normalizeBudgetMonth(item.month) === normalizedMonth
    && (item.businessUnit || null) === businessUnit,
  );
  const relevantTransactions = transactions.filter((item) =>
    !item.deletedAt && item.direction === "DEBIT"
    && item.date.slice(0, 7) === monthKey
    && (businessUnit === null || matchesBrandFilter(item, businessUnit)),
  );
  const actualByCategory = new Map<string, number>();
  for (const item of relevantTransactions) {
    actualByCategory.set(item.category, (actualByCategory.get(item.category) || 0) + item.amount);
  }
  const budgetByCategory = new Map(selectedBudgets.map((item) => [item.category, item.amount]));
  const categories = [...new Set([...budgetByCategory.keys(), ...actualByCategory.keys()])];
  const items = categories.map((category) => {
    const budget = budgetByCategory.get(category) || 0;
    const actual = actualByCategory.get(category) || 0;
    return {
      category,
      budget,
      actual,
      remaining: budget - actual,
      usagePercent: budget ? (actual / budget) * 100 : null,
    };
  }).sort((a, b) => b.actual - a.actual || b.budget - a.budget);
  const budget = items.reduce((sum, item) => sum + item.budget, 0);
  const actual = items.reduce((sum, item) => sum + item.actual, 0);
  return {
    month: normalizedMonth,
    businessUnit,
    budget,
    actual,
    remaining: budget - actual,
    usagePercent: budget ? (actual / budget) * 100 : null,
    items,
  };
}

export function budgetTone(usagePercent: number | null) {
  if (usagePercent === null) return "neutral" as const;
  if (usagePercent > 100) return "over" as const;
  if (usagePercent >= 85) return "warning" as const;
  return "safe" as const;
}
